/**
 * userAndStoreContext API
 *
 * Provides user and store context information for tools.
 * getUserPermissions is Platform-internal.
 *
 * Store model types come from @amzn/coral_com-amazon-jwostoremanagementgateway
 * (JwoStoreManagementGatewayTypeScriptClient).
 */

import { Action } from '@amzn/jwo-store-management-policy-model-javascript'
import { TtlCache } from '@api/utils/TtlCache'
import { authService } from '@services/auth/AuthService'
import { decodeJwtPayload } from '@utils/jwtUtils'
// Import individual model files to avoid pulling in JwoStoreManagementGateway
// (which has transitive deps that break Jest resolution).
import type { StoreDetails } from '@amzn/coral_com-amazon-jwostoremanagementgateway/StoreDetails'
import type { StoreActions } from '@amzn/coral_com-amazon-jwostoremanagementgateway/StoreActions'
import type { ListStoreApiOutput } from '@amzn/coral_com-amazon-jwostoremanagementgateway/ListStoreApiOutput'
import type { GetStoreApiOutput } from '@amzn/coral_com-amazon-jwostoremanagementgateway/GetStoreApiOutput'
import { ListStoreApiInput } from '@amzn/coral_com-amazon-jwostoremanagementgateway/ListStoreApiInput'
import { GetStoreApiInput } from '@amzn/coral_com-amazon-jwostoremanagementgateway/GetStoreApiInput'
import { Role } from '@amzn/coral_com-amazon-jwostoremanagementgateway/Role'
import { getGatewayClientForRealm, getServiceClient } from '@api/client-factory/StoreManagementClientFactory'
import { forgetStoreRegion, getStoreRegion, recordStoreRegion } from '@services/StoreRegionRegistry'
import { isCoralException } from '@utils/CoralErrors'
import { getActiveRealm, getPeerRealms, toAirportCode, toRealm } from '@utils/RegionRealms'

export { Action, Role }

// Re-export gateway model types so existing consumers keep working.
export type { StoreDetails, StoreActions, ListStoreApiOutput, GetStoreApiOutput }

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface IUserPermissions {
    role: string
    allowedActions: Action[]
}

export interface IUserAndStoreContext {
    userAlias: string
    store: GetStoreApiOutput
}

// ---------------------------------------------------------------------------
// Caches — all declared together at the top
// ---------------------------------------------------------------------------

const permissionsCache = new TtlCache<IUserPermissions>(30 * 60 * 1000) // 30 min
const userAliasCache = new TtlCache<string>(60 * 60 * 1000)             // 1 hour, single session entry
const userStoresCache = new TtlCache<StoreActions[]>(60 * 60 * 1000)    // 1 hour

// ---------------------------------------------------------------------------
// Cache clear helpers
// ---------------------------------------------------------------------------

/** Clear cached permissions for a specific store (call on store switch). */
export function clearPermissionsCache (storeId: string): void {
    permissionsCache.delete(storeId)
}

/** Clear all cached permissions (call on login). */
export function clearAllPermissionsCache (): void {
    permissionsCache.clear()
}

/** Clear cached user alias (call on login). */
export function clearUserAliasCache (): void {
    userAliasCache.clear()
}

/** Clear cached stores for a specific user or all users. */
export function clearUserStoresCache (userAlias?: string): void {
    if (userAlias) {
        userStoresCache.delete(userAlias)
        console.log(`[getUserStores] Cleared cache for ${userAlias}`)
    } else {
        userStoresCache.clear()
        console.log('[getUserStores] Cleared all cache')
    }
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Current user permissions (from GetCurrentUser API)
// ---------------------------------------------------------------------------

let actionsByResource: Record<string, string[]> = {}
let storeAssignments: Array<{ storeId: string; role: string; expiresAt?: Date | number }> = []
let allAllowedActions: string[] = []
let isAdminUser = false
// Orgs the caller belongs to (id = organizationId), from the GetCurrentUser response. Used by
// the userManagement wrappers to default the org on org-scoped requests when a tool omits it.
let callerOrganizations: Array<{ id?: string; name?: string }> = []
let currentUserLoaded = false
let currentUserFailed = false
const currentUserListeners: Array<() => void> = []
const INSTANCE_RESOURCE = 'Instance'

// --- Mid-session revalidation (P465134335) --------------------------------
// Triggered from App on tab-focus and from CoralRequestExecutor on any 403.
// Detects access revocation that happened after the initial login-time fetch
// (Field Technician expiry, admin-disabled user, etc.) by re-running
// getCurrentUser and letting hasNoAssignedPermissions() flip true, which
// causes App to render PermissionsRequiredPage.
const REVALIDATION_COOLDOWN_MS = 30 * 1000
// Minimum interval applied even to the forced (403-triggered) path — bounds
// worst-case QPS when 403s arrive serially past the main cooldown window
// (e.g. sporadic auth failures on a polling resource). AutoSDE flagged this
// on CR-295041581: without a floor, force: true could drive one full
// GetCurrentUser fetch per 403 indefinitely. 5s cap ≈ 12 forced revalidations
// per minute per user in the worst case, negligible backend load.
const FORCED_REVALIDATION_MIN_INTERVAL_MS = 5 * 1000
let lastRevalidatedAt: number = 0
// Persistent change listeners — fire after every getCurrentUser fetch
// resolves (success OR failure). Distinct from currentUserListeners (which
// is one-shot for the initial load).
const currentUserChangeListeners: Array<() => void> = []
// Re-entrancy guard for revalidateCurrentUser. Prevents an infinite loop
// when the GetCurrentUser call fired by a revalidation itself returns 403
// (fully-revoked Cognito session, disabled user, etc.): the CoralRequestExecutor
// 403 handler would otherwise re-invoke revalidateCurrentUser while we're
// still awaiting the outer call, starting a request storm. `initPromise`
// alone can't gate this because it remains truthy as a cache after the
// initial load — this flag is a dedicated "revalidation actively in flight"
// signal.
let revalidationInFlight = false

// --- Time-bound expiry timer (P465134335) ----------------------------------
// The tab-focus and 403 triggers above are not enough on their own. In the
// native shells the WebView is never told the app was backgrounded, so
// visibilitychange does not fire on a phone; and tools that build their own
// Coral client (Price Check, Planogram — "query param" model in AGENTS.md)
// call the backend directly, so their 403s never reach the platform's
// CoralRequestExecutor. So we also re-check at the moment the earliest
// time-bound assignment expires. GetCurrentUser then strips it server-side
// and, if it was the user's only access, hasNoAssignedPermissions() flips and
// App shows PermissionsRequiredPage.
//
// Grace past expiresAt so the server's `expiresAt.isBefore(now)` check has
// passed by the time our request lands, allowing for small clock skew.
const EXPIRY_REVALIDATION_GRACE_MS = 1000
// setTimeout overflows (fires immediately) past 2^31-1 ms (~24.8 days).
const MAX_TIMER_DELAY_MS = 2 ** 31 - 1
let expiryTimer: ReturnType<typeof setTimeout> | null = null

function toEpochMillis (expiresAt: Date | number | undefined): number | null {
    if (expiresAt instanceof Date) return expiresAt.getTime()
    // Epoch seconds, matching the tool-facing StoreAssignment.expiresAt shape.
    if (typeof expiresAt === 'number') return expiresAt * 1000
    return null
}

function clearExpiryTimer (): void {
    if (expiryTimer) {
        clearTimeout(expiryTimer)
        expiryTimer = null
    }
}

/**
 * (Re)arm a single timer for the earliest time-bound assignment expiry. Called
 * after every GetCurrentUser fetch, so the timer always tracks the latest state.
 * An expiry already in the past (server clock behind ours, or the last fetch
 * failed) is retried after REVALIDATION_COOLDOWN_MS rather than immediately, so
 * a persistent skew can't turn into a request loop.
 */
function armExpiryRevalidation (): void {
    clearExpiryTimer()
    const expiries = storeAssignments
        .map((a) => toEpochMillis(a.expiresAt))
        .filter((ms): ms is number => ms !== null && Number.isFinite(ms))
    if (expiries.length === 0) return

    const earliest = Math.min(...expiries)
    const now = Date.now()
    const delay = earliest > now
        ? Math.min(earliest - now + EXPIRY_REVALIDATION_GRACE_MS, MAX_TIMER_DELAY_MS)
        : REVALIDATION_COOLDOWN_MS
    expiryTimer = setTimeout(() => {
        expiryTimer = null
        // A fetch already in flight re-arms from its own finally block.
        if (revalidationInFlight || (initPromise && !currentUserLoaded)) return
        void revalidateCurrentUser({ force: true }).then(() => {
            // Re-arm if the call was throttled and never fetched.
            if (!expiryTimer) armExpiryRevalidation()
        })
    }, delay)
}

// Signature of the last-observed current-user state. Used to suppress
// change-listener notifications when a revalidation returns data identical to
// what we already have.
//
// Why this matters beyond avoiding a wasted render: on Android/iOS, launching
// a native Activity (camera, document picker) fires `visibilitychange` on
// return, which triggers revalidateCurrentUser(). Notifying unconditionally
// re-rendered App, which tore down the Store Health View iframe and lost
// in-flight state such as a pending media upload. Notifying only on real
// change makes revalidation non-destructive. Reported by hrisawan against
// CR-295041581.
//
// null means "no fetch has completed yet" — the first fetch always notifies so
// the initial-load permissions gate still fires for a user who genuinely has
// no assignments.
let lastUserStateSignature: string | null = null

/**
 * Build a stable, order-independent signature of the state that consumers of
 * onCurrentUserChanged can actually observe.
 *
 * Everything feeding hasNoAssignedPermissions(), hasTimeBoundAssignment(),
 * isFieldTech() and getUserPermissions() must be represented here — if a field
 * is omitted, a real change in it would be silently swallowed and the
 * mid-session revocation gate (P465134335) would stop firing. Time-bound
 * (Field Technician) expiry needs no special handling: GetCurrentUserActivity
 * strips expired assignments and their actions server-side (P467955166), so a
 * lapsed assignment already shows up here as a plain content change.
 *
 * Keys and arrays are sorted because ordering is not contractually stable —
 * actionsByResource is assembled from Mosaic ListAccessProfileAssignments
 * results, whose pagination order is not guaranteed. Without normalising, a
 * reordered-but-equivalent response would register as a change and reintroduce
 * the very re-render this function exists to prevent.
 */
function computeUserStateSignature (): string {
    const actions = Object.keys(actionsByResource)
        .sort()
        .map((resource) => `${resource}:${[...(actionsByResource[resource] ?? [])].sort().join(',')}`)
        .join('|')

    const assignments = storeAssignments
        .map((a) => `${a.storeId}:${a.role}`)
        .sort()
        .join('|')

    return [
        `failed=${String(currentUserFailed)}`,
        `admin=${String(isAdminUser)}`,
        `actions=${actions}`,
        `assignments=${assignments}`,
    ].join(';')
}

let initPromise: Promise<void> | null = null

// Increments whenever a new GetCurrentUser fetch starts. A fetch abandoned by
// refetchCurrentUser's deadline can still be in flight when a newer fetch launches; comparing
// generations lets the superseded fetch's eventual result be discarded instead of
// last-write-wins clobbering the newer state.
let fetchGeneration = 0

/** Fetch current user permissions from backend. Safe to call multiple times — only fetches once per session. */
export function initCurrentUser (): Promise<void> {
    if (initPromise) return initPromise
    fetchGeneration += 1
    const generation = fetchGeneration
    initPromise = (async () => {
        try {
            const { getCurrentUser } = require('@api/userManagement')
            const result = await getCurrentUser()
            if (generation !== fetchGeneration) {
                console.warn('[userAndStoreContext] Discarding a superseded user fetch result')
                return
            }
            actionsByResource = result.actionsByResource || {}
            storeAssignments = result.storeAssignments || []
            callerOrganizations = result.organizations || []
            allAllowedActions = [...new Set(Object.values(actionsByResource).flat())]
            isAdminUser = result.isAdmin ?? false
            currentUserFailed = false
            lastRevalidatedAt = Date.now()
            console.info('[userAndStoreContext] Fetched current user:', {
                id: result.id,
                isAdmin: result.isAdmin,
                actionsByResource,
            })
        } catch (err) {
            if (generation !== fetchGeneration) {
                console.warn('[userAndStoreContext] Discarding a superseded user fetch failure', err)
                return
            }
            console.error('[userAndStoreContext] Failed to fetch current user', err)
            currentUserFailed = true
            // Allow retry on next call
            initPromise = null
        } finally {
            // A superseded fetch (see fetchGeneration) must not flip flags or notify anyone —
            // the generation that superseded it owns those effects.
            if (generation === fetchGeneration) {
                currentUserLoaded = true
                currentUserListeners.forEach((cb) => cb())
                currentUserListeners.length = 0
                // Persistent change listeners — not cleared here, so they survive
                // across revalidations. Notified ONLY when the observable user state
                // actually changed (or on the very first fetch), so that a
                // revalidation returning identical data is a no-op for the UI.
                // A spurious notification re-renders App and destroys tool iframes,
                // losing in-flight tool state (e.g. a pending SHV media upload on
                // Android, where returning from the native camera fires
                // visibilitychange). See computeUserStateSignature.
                armExpiryRevalidation()
                const signature = computeUserStateSignature()
                const changed = lastUserStateSignature === null || signature !== lastUserStateSignature
                lastUserStateSignature = signature
                if (changed) {
                    currentUserChangeListeners.slice().forEach((cb) => {
                        try { cb() } catch (e) { console.warn('[userAndStoreContext] change listener threw', e) }
                    })
                }
            }
        }
    })()
    return initPromise
}

/**
 * Await the user fetch until the one awaited is still the live one. A fetch can be superseded
 * mid-wait (refetchCurrentUser's deadline abandons it and a newer fetch starts), and a superseded
 * fetch writes nothing — a caller resuming on it would read the pre-fetch state.
 */
async function awaitLiveFetch (): Promise<void> {
    let awaited: Promise<void> | null = null
    while (initPromise && initPromise !== awaited) {
        awaited = initPromise
        await awaited
    }
}

/** Whether the current user fetch failed. */
export function isCurrentUserFailed (): boolean {
    return currentUserFailed
}

/** Reset current user state. Call on logout/session change. */
export function resetCurrentUser (): void {
    actionsByResource = {}
    storeAssignments = []
    allAllowedActions = []
    isAdminUser = false
    callerOrganizations = []
    currentUserLoaded = false
    currentUserFailed = false
    currentUserListeners.length = 0
    currentUserChangeListeners.length = 0
    lastRevalidatedAt = 0
    revalidationInFlight = false
    lastUserStateSignature = null
    clearExpiryTimer()
    initPromise = null
    // Supersede any fetch still in flight: it belongs to the session being reset, and landing
    // afterwards would repopulate the previous user's permissions and flip currentUserLoaded.
    fetchGeneration += 1
    permissionsCache.clear()
}

/** Get all allowed actions (union across all resources). */
export function getAllAllowedActions (): string[] {
    return allAllowedActions
}

/** Get per-resource action map. */
export function getActionsByResource (): Record<string, string[]> {
    return actionsByResource
}

/** Whether the current user is an admin. */
export function isCurrentUserAdmin (): boolean {
    return isAdminUser
}

/**
 * The ids of the caller's organizations from the cached GetCurrentUser result (empty for a legacy
 * caller or before initCurrentUser() has resolved). Used to check a tool-supplied organizationId.
 */
export function getCallerOrganizationIds (): string[] {
    return callerOrganizations
        .map((o) => o.id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0)
}

/**
 * The caller's default organizationId, or undefined when they belong to no org.
 *
 * The userManagement API wrappers use this to default the org on org-scoped requests when the
 * calling tool didn't supply one, so Golden Gate's authz layer (AVP) — which routes by
 * organizationId and runs before the activity — always has a tenant to route by. A request that
 * reaches authz with no org fails authorization (nismahto@, CR-298413040), which is why an
 * embedded IdP-config tool omitting the org broke SetupIdentityProvider.
 *
 * For a multi-org caller we return the FIRST org, matching the interim behaviour on the backend
 * (a proper org-picker is deferred). This is a routing default, not the isolation boundary: the
 * caller's org set is derived server-side from the immutable custom:organizationIds token claim,
 * and every org-scoped op is still authorized server-side (AVP/Cedar + OrgBoundaryGuard membership
 * check), so a caller can never route to an org they don't belong to. A tool that supplies an
 * explicit org always overrides this default. Reads the GetCurrentUser result cached by
 * initCurrentUser(), so callers should ensure that has run (the wrappers await it).
 */
export function getResolvedOrganizationId (): string | undefined {
    // Multi-org: default to the first org (interim, matches the backend; an explicit
    // tool-supplied org wins upstream in resolveOrganizationId).
    return getCallerOrganizationIds()[0]
}

/**
 * Whether the signed-in user has no assigned permissions: the current-user
 * fetch has loaded successfully (not failed), the user is not an admin, and
 * they have no allowed actions. Used to surface the PermissionsRequiredPage
 * after login instead of an empty screen. Returns false while loading or on
 * fetch failure (fail-open) so we never misreport a transient error as a
 * permissions gap.
 */
export function hasNoAssignedPermissions (): boolean {
    return currentUserLoaded && !currentUserFailed && !isAdminUser && allAllowedActions.length === 0
}

/** Whether the current user has any time-bound (TechInstaller) store assignment. */
export function hasTimeBoundAssignment (): boolean {
    return storeAssignments.some((a) => a.role === 'TechInstaller')
}

/**
 * Whether the signed-in user is a field tech, i.e. has a TechInstaller store assignment.
 *
 * NOTE: this is a role check, not a capability check. TechInstaller happens to be the only
 * time-bound role today, but time-bound access could be extended to other roles in future — so
 * "is this user a field tech" is an approximation, not a foolproof gate. If a stronger guarantee is
 * ever needed (e.g. an explicit persona or capability flag), prefer that over matching on role here.
 */
export function isFieldTech (): boolean {
    return storeAssignments.some((a) => a.role === Role.TechInstaller)
}

export const MANAGER_ACTIONS = ['ListUsers', 'UpdateUserRole']

/** Whether the current user is an admin or store manager at any store. */
export async function isAdminOrStoreManager (): Promise<boolean> {
    initCurrentUser()
    await awaitLiveFetch()

    if (isAdminUser) return true
    if (storeAssignments.some((a) => a.role === 'StoreManager')) return true

    // If cached data loaded successfully, trust it — no fallback needed
    if (currentUserLoaded && !currentUserFailed) return false

    // Fallback: check store actions from the store list API
    try {
        const alias = await getUserAlias()
        const stores = await getUserStores(alias)
        return stores.some((store) =>
            store.actions?.some((action: string) => MANAGER_ACTIONS.includes(action)),
        )
    } catch (err) {
        console.warn('[userAndStoreContext] isAdminOrStoreManager: failed to check store actions', err)
        return false
    }
}

/** Whether the current user data has been loaded. */
export function isCurrentUserLoaded (): boolean {
    return currentUserLoaded
}

/** Register a one-time callback for when current user data is loaded. */
export function onCurrentUserLoaded (cb: () => void): void {
    if (currentUserLoaded) {
        cb()
    } else {
        currentUserListeners.push(cb)
    }
}

/**
 * Subscribe to current-user state changes.
 *
 * Fires after an initCurrentUser or revalidateCurrentUser fetch resolves, but
 * ONLY when the observable user state actually changed (permissions, store
 * assignments, admin flag, or fetch-failure state). The first completed fetch
 * always fires. A revalidation that returns identical data does not.
 *
 * That suppression is deliberate and load-bearing, not just an optimisation:
 * subscribers typically force a re-render, which tears down mounted tool
 * iframes and discards their in-flight state. On Android/iOS, returning from a
 * native Activity (camera, document picker) fires `visibilitychange` and thus a
 * revalidation, so an unconditional notification would kill an in-progress
 * media upload in Store Health View.
 *
 * Unlike onCurrentUserLoaded (one-shot), this listener persists across multiple
 * fetches, so it's suitable for surfacing session revocation to the UI.
 * Returns an unsubscribe function.
 *
 * P465134335.
 */
export function onCurrentUserChanged (cb: () => void): () => void {
    currentUserChangeListeners.push(cb)
    return () => {
        const i = currentUserChangeListeners.indexOf(cb)
        if (i >= 0) currentUserChangeListeners.splice(i, 1)
    }
}

/**
 * Re-fetch the current user's permissions, bypassing the once-per-session
 * initPromise short-circuit. Detects mid-session access revocation:
 * Field Technician expiry passed, admin disabled the user, etc. On the
 * resulting hasNoAssignedPermissions() flip, App renders PermissionsRequiredPage.
 *
 * Rate-limited: unless force=true, calls within REVALIDATION_COOLDOWN_MS of
 * the last successful revalidation are no-ops. Prevents thundering-herd
 * from rapid tab-focus events. force=true is used from the 403 interceptor
 * path — a 403 is a strong signal to re-check regardless of when we last did,
 * but still respects FORCED_REVALIDATION_MIN_INTERVAL_MS to bound worst-case
 * QPS when 403s arrive serially past the main cooldown (AutoSDE flag on
 * CR-295041581).
 *
 * If a fetch is already in flight (initial load OR a prior revalidation),
 * coalesces into that fetch by returning the same promise — awaiting callers
 * observe fresh data when the outer fetch completes, so no caller proceeds
 * with stale state.
 *
 * P465134335.
 */
export async function revalidateCurrentUser (opts: { force?: boolean } = {}): Promise<void> {
    // Re-entrancy guard: if a revalidation is already awaiting getCurrentUser,
    // return the in-flight promise so awaiters observe fresh state when it
    // resolves (rather than returning `undefined` before the outer fetch
    // completes — AutoSDE docstring-consistency flag on CR-295041581).
    // Also prevents an infinite loop when getCurrentUser itself returns 403
    // (revoked session) and re-triggers the 403 handler while the outer
    // revalidation is still in flight — the re-entrant caller gets the same
    // promise the outer caller already holds; no new fetch fires.
    if (revalidationInFlight) return initPromise ?? undefined
    // Initial-load fetch in flight — piggyback on the current one.
    // After a completed fetch `initPromise` still holds the resolved promise
    // as a cache; we distinguish "in flight" from "cached" via `currentUserLoaded`
    // (set to true in the fetch's finally block).
    if (initPromise && !currentUserLoaded) return initPromise
    // Cooldown: main 30s cap for unforced (tab-focus) triggers; shorter 5s
    // cap for forced (403-triggered) triggers.
    const now = Date.now()
    const minIntervalMs = opts.force ? FORCED_REVALIDATION_MIN_INTERVAL_MS : REVALIDATION_COOLDOWN_MS
    if (lastRevalidatedAt > 0 && (now - lastRevalidatedAt) < minIntervalMs) {
        return
    }
    revalidationInFlight = true
    // Reset the initPromise so initCurrentUser re-fetches instead of no-op'ing
    initPromise = null
    try {
        await initCurrentUser()
    } finally {
        revalidationInFlight = false
    }
}

// Bounds how long refetchCurrentUser keeps revalidationInFlight raised: GetCurrentUser is a bare
// fetch with no client timeout, and a hung one must not wedge every later 403/tab-focus
// revalidation for the session. On timeout the fetch keeps running in the background — its own
// finally still updates state and fires the change listeners when it eventually settles.
export const REFETCH_FETCH_DEADLINE_MS = 10000

export type RefetchResult = 'ok' | 'failed' | 'timeout'

/**
 * Unconditionally re-fetch the current user's permissions, waiting out any fetch already in
 * flight instead of coalescing into it.
 *
 * The account-setup gate's release path needs a stronger contract than revalidateCurrentUser:
 * after the backend re-keys a migrated user, the cached state — or an initial fetch that started
 * pre-completion — still says "no roles", so piggybacking on the in-flight fetch or being
 * throttled by the 5s forced floor would release the gate onto stale data and strand the user on
 * PermissionsRequiredPage. This always performs a fresh fetch; the 403/tab-focus cooldown paths
 * are untouched because revalidateCurrentUser is unchanged. A 403-forced revalidation arriving
 * while this is in flight coalesces onto it via revalidationInFlight, and the fetch re-stamps
 * lastRevalidatedAt, so the forced-path QPS bound survives.
 *
 * `while`, not `if`: when a prior revalidation's fetch settles, our await can resume before that
 * revalidation's finally clears revalidationInFlight; proceeding then would start a fetch whose
 * re-entrancy guard the old finally clears mid-flight. Re-checking until both conditions clear
 * relies on the invariant that revalidationInFlight=true means initPromise is the live fetch
 * (or, in the failed-fetch window, that the clearing finally is already queued).
 */
export async function refetchCurrentUser (timeoutMs: number = REFETCH_FETCH_DEADLINE_MS): Promise<RefetchResult> {
    while (revalidationInFlight || (initPromise && !currentUserLoaded)) {
        await initPromise
    }
    revalidationInFlight = true
    initPromise = null
    let deadlineHandle: ReturnType<typeof setTimeout> | undefined
    try {
        const raced = await Promise.race([
            initCurrentUser().then(() => 'settled' as const),
            new Promise<'timeout'>((resolve) => {
                deadlineHandle = setTimeout(() => resolve('timeout'), timeoutMs)
            }),
        ])
        if (raced === 'timeout') {
            // Drop the hung fetch from initPromise, or — when it was the session's first fetch,
            // so currentUserLoaded is still false — every later caller that awaits initPromise
            // (this function's wait-out loop, revalidateCurrentUser, getUserPermissions) wedges
            // on it for the rest of the session. The fetch itself keeps running: the generation
            // guard governs its eventual effects, and the next fetch starts cleanly.
            initPromise = null
            return 'timeout'
        }
        return currentUserFailed ? 'failed' : 'ok'
    } finally {
        clearTimeout(deadlineHandle)
        revalidationInFlight = false
    }
}

// ---------------------------------------------------------------------------
// getUserPermissions
// ---------------------------------------------------------------------------

/**
 * Get the current user's permissions at a given store.
 *
 * Platform-internal only — used for menu filtering, store-switch handling,
 * and role-based UI. userId is resolved from the authenticated session.
 *
 * Results are sourced from the GetCurrentUser API response cached in
 * userAndStoreContext. Falls back to instance-level permissions if no
 * store-specific permissions exist.
 */
export async function getUserPermissions (storeId: string): Promise<IUserPermissions> {
    if (!storeId) {
        throw new Error('[getUserPermissions] storeId is required')
    }

    const cached = permissionsCache.get(storeId)
    if (cached) {
        console.log(`[getUserPermissions] Cache hit for store ${storeId}`)
        return cached
    }

    console.log(`[getUserPermissions] Resolving permissions for store ${storeId}`)

    // Ensure current user data is loaded before reading; retry if previous attempt failed
    if (!initPromise && (!currentUserLoaded || currentUserFailed)) {
        initCurrentUser()
    }
    await awaitLiveFetch()

    const storeActions = actionsByResource[storeId] || []
    const instanceActions = actionsByResource[INSTANCE_RESOURCE] || []
    const merged = [...new Set([...instanceActions, ...storeActions])]

    const permissions: IUserPermissions = {
        role: storeAssignments.find((a) => a.storeId === storeId)?.role ?? 'Associate',
        allowedActions: merged as Action[],
    }

    permissionsCache.set(storeId, permissions)

    return permissions
}

// ---------------------------------------------------------------------------
// getUserAndStoreContext
// ---------------------------------------------------------------------------

/**
 * Get user and store context information
 *
 * @param storeId - Store identifier
 * @returns Promise<IUserAndStoreContext> - User alias and store details
 */
export async function getUserAndStoreContext (storeId: string): Promise<IUserAndStoreContext> {
    console.log(`[getUserAndStoreContext] Getting context for store ${storeId}`)

    const [userAlias, store] = await Promise.all([
        getUserAlias(),
        getStoreInformation(storeId),
    ])

    return { userAlias, store }
}

// ---------------------------------------------------------------------------
// getUserAlias
// ---------------------------------------------------------------------------

/**
 * Get the current user's alias from the authenticated session.
 * Cached for 1 hour. Alias is session-scoped, not store-scoped.
 *
 * @returns Promise<string> - The user alias
 */
export async function getUserAlias (): Promise<string> {
    const cached = userAliasCache.get('session')
    if (cached) {
        return cached
    }

    console.log('[getUserAlias] Getting user alias')

    const token = await authService.getAccessToken()
    const payload = decodeJwtPayload(token)
    if (!payload || !payload.sub) {
        throw new Error('[getUserAlias] Could not extract sub from access token')
    }
    const alias = payload.sub
    userAliasCache.set('session', alias)
    return alias
}

// ---------------------------------------------------------------------------
// getStoreInformation
// ---------------------------------------------------------------------------

/**
 * Records the cell a store lives in, so later calls for it skip the search.
 *
 * Prefers the region GetStore reports over the cell that answered. The reported field is read
 * defensively because the currently published coral client predates it and its fromJson drops
 * undeclared fields — so it yields undefined today and takes over once the client is regenerated.
 */
function rememberStoreCell (storeId: string, servingRealm: string | undefined, output: GetStoreApiOutput): GetStoreApiOutput {
    const reportedRegion = (output.storeDetails as { region?: string } | undefined)?.region
    recordStoreRegion(storeId, toAirportCode(reportedRegion) ?? toAirportCode(servingRealm))
    return output
}

/**
 * Get store information for the specified store.
 *
 * Only the cell owning a store can describe it, answering ResourceNotFoundException everywhere else,
 * and that cell is not necessarily the one we authenticated against (AGENTS.md, "Region and Cell
 * Routing"). So an unseen store is looked up in the cell we are pinned to and then in its US peer,
 * and the answer is remembered so later calls — and everything getRegion drives — go straight there.
 *
 * @param storeId - Store identifier
 * @returns Promise<GetStoreApiOutput> - Store details from gateway
 */
export async function getStoreInformation (storeId: string): Promise<GetStoreApiOutput> {
    console.log(`[getStoreInformation] Getting store info for ${storeId}`)

    const input = GetStoreApiInput.clone({ storeId })
    const knownRealm = toRealm(getStoreRegion(storeId))
    // Cells already asked, so a store that is genuinely missing costs one call per cell and no more.
    const tried = new Set<string | undefined>()
    let lastError: any

    if (knownRealm) {
        tried.add(knownRealm)
        try {
            const output = await getGatewayClientForRealm(knownRealm).getStoreApi(input).toPromise()
            return rememberStoreCell(storeId, knownRealm, output)
        } catch (err: any) {
            if (!isCoralException(err, 'ResourceNotFoundException')) throw err
            // A store the recorded cell no longer recognises has been re-homed; search again.
            console.warn(`[getStoreInformation] ${storeId} no longer in ${knownRealm} — re-resolving`)
            forgetStoreRegion(storeId)
            lastError = err
        }
    }

    // Read after the block above: forgetStoreRegion can move the session back off a stale cell.
    const activeRealm = getActiveRealm()
    if (!tried.has(activeRealm)) {
        tried.add(activeRealm)
        try {
            // getServiceClient rather than a realm-specific client so ?useVirgo=true still applies.
            // The peer search below is gateway-only; the Virgo override is a debug path.
            const output = await getServiceClient().getStoreApi(input).toPromise()
            return rememberStoreCell(storeId, activeRealm, output)
        } catch (err: any) {
            if (!isCoralException(err, 'ResourceNotFoundException')) throw err
            lastError = err
        }
    }

    const peerRealms = getPeerRealms().filter((realm) => !tried.has(realm))
    if (peerRealms.length > 0) {
        console.warn(`[getStoreInformation] ${storeId} not found in the pinned cell; trying ${peerRealms.join(', ')}`)
    }
    for (const realm of peerRealms) {
        try {
            const output = await getGatewayClientForRealm(realm).getStoreApi(input).toPromise()
            console.log(`[getStoreInformation] ${storeId} resolved in ${realm}`)
            return rememberStoreCell(storeId, realm, output)
        } catch (err: any) {
            // Only "not here" means keep looking. Throttling or a 5xx from the cell that does own the
            // store has to surface as itself — swallowing it reports the search as a not-found from
            // whichever cell happened to answer last, which hides the actual fault.
            if (!isCoralException(err, 'ResourceNotFoundException')) throw err
            console.warn(`[getStoreInformation] ${storeId} not in ${realm}`, err)
            lastError = err
        }
    }

    throw lastError
}

/**
 * Resolves and records which cell owns `storeId`, if it is not already known.
 *
 * Selecting a store dispatches synchronously and the picker can navigate straight to a tool, so
 * without this the iframe URL is built while the store's cell is still unknown and silently points
 * at the cell we signed in against — with no rebuild to correct it. Call this before publishing a
 * new active store.
 *
 * Errors are swallowed: this is a pre-warm, and the caller's own request is what should surface a
 * store that cannot be read. getRegion then behaves exactly as it did before, falling back to the
 * pinned cell.
 */
export async function ensureStoreRegionKnown (storeId: string | undefined): Promise<void> {
    if (!storeId || getStoreRegion(storeId)) {
        return
    }
    try {
        await getStoreInformation(storeId)
    } catch (err) {
        console.warn(`[ensureStoreRegionKnown] Could not resolve the cell owning ${storeId}`, err)
    }
}

// ---------------------------------------------------------------------------

/**
 * Get list of stores the authenticated user has access to.
 * The backend resolves the caller from the auth token — userAlias is not
 * sent to the API but is used as a cache key so that a re-login as a
 * different user invalidates the cached result.
 *
 * Results are cached in memory for 1 hour.
 */
export async function getUserStores (userAlias: string): Promise<StoreActions[]> {
    const cached = userStoresCache.get(userAlias)
    if (cached) {
        console.log(`[getUserStores] Returning cached stores for ${userAlias}`)
        return cached
    }

    console.log(`[getUserStores] Fetching stores for ${userAlias} from backend`)

    try {
        const client = getServiceClient()
        const listResult = await client.listStoreApi(
            ListStoreApiInput.clone({}),
        ).toPromise()
        const stores = listResult.stores || []

        // ListStoreApi only returns storeId + actions — hydrate storeDetails via getStoreInformation.
        // That hydration is also how each store's cell is learned, since ListStores does not report
        // one: a store outside the pinned cell costs one extra lookup here and none afterwards.
        // If GetStore returns NotAuthorizedException (e.g. expired time-bound access), exclude the
        // store from the list — the user can no longer access it.
        const storesWithDetails = (await Promise.all(stores.map(async (store) => {
            if (store.storeDetails?.storeName || !store.storeId) return store
            try {
                const detail = await getStoreInformation(store.storeId)
                return { ...store, storeDetails: detail.storeDetails } as StoreActions
            } catch (err: any) {
                // A NotAuthorizedException means time-bound access has expired.
                if (isCoralException(err, 'NotAuthorizedException')) {
                    console.warn(`[getUserStores] Access denied for store ${store.storeId} — excluding from list`)
                    return null
                }
                console.warn(`Failed to get details for ${store.storeId}`, err)
                return store
            }
        }))).filter((store): store is StoreActions => store !== null)

        userStoresCache.set(userAlias, storesWithDetails)
        return storesWithDetails
    } catch (error: any) {
        console.error(`listStoreApi FAILED:`, error)
        throw error
    }
}

// ---------------------------------------------------------------------------
// Merchant grouping
// ---------------------------------------------------------------------------

export interface IMerchantInfo {
    merchantId: string
    storeIds: string[]
}

export interface IStoreWithoutMerchant {
    storeId: string
    /** GetStore populates storeName with the store's externalStoreId. */
    storeName?: string
}

export interface IGetUserMerchantsResult {
    /** Sorted by merchantId. */
    merchants: IMerchantInfo[]
    /** Stores whose details loaded but carry no merchantId. */
    storesWithoutMerchant: IStoreWithoutMerchant[]
    /** Stores whose GetStore lookup failed, so their merchant is unknown. */
    storeIdsWithoutDetails: string[]
}

export async function getUserMerchants (userAlias: string): Promise<IGetUserMerchantsResult> {
    const stores = await getUserStores(userAlias)
    const merchantMap = new Map<string, string[]>()
    const storesWithoutMerchant: IStoreWithoutMerchant[] = []
    const storeIdsWithoutDetails: string[] = []

    for (const store of stores) {
        // storeId is always set by the backend; the generated Coral type just marks it optional.
        if (!store.storeId) continue
        // getUserStores returns the bare ListStore entry (no storeDetails) when GetStore fails.
        if (!store.storeDetails) {
            storeIdsWithoutDetails.push(store.storeId)
            continue
        }
        const merchantId = store.storeDetails.merchantId
        if (!merchantId) {
            storesWithoutMerchant.push({ storeId: store.storeId, storeName: store.storeDetails.storeName })
            continue
        }

        const existing = merchantMap.get(merchantId)
        if (existing) {
            existing.push(store.storeId)
        } else {
            merchantMap.set(merchantId, [store.storeId])
        }
    }

    if (storesWithoutMerchant.length > 0) {
        console.warn(`[getUserMerchants] ${storesWithoutMerchant.length} store(s) have no merchantId:`, storesWithoutMerchant)
    }
    if (storeIdsWithoutDetails.length > 0) {
        console.warn(`[getUserMerchants] ${storeIdsWithoutDetails.length} store(s) have no details, merchant unknown:`, storeIdsWithoutDetails)
    }

    return {
        merchants: Array.from(merchantMap.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([merchantId, storeIds]) => ({ merchantId, storeIds })),
        storesWithoutMerchant,
        storeIdsWithoutDetails,
    }
}
